import {SKZEnvironment} from '@/utils/SKZEnvironment.types';
export const CSRF_HEADER = 'X-CSRF-Token';
export const USER_ID_HEADER = 'x-skillz-user-id';
export const USER_LOCATION_KEY = 'skillz-user-location';
export const CLIENT_IP_HEADER = 'true-client-ip';
export const GAME_SETTINGS_LS_NAME = '_sklz_gs_';
export const GAME_UNITY_SDK_LS_NAME = '_sklz_gus';
export const CURRENT_GAME_COOKIE_NAME = '_sklz_cg';

export function getCurrentGameCookieName(gameId: string | number) {
  return `${CURRENT_GAME_COOKIE_NAME}_${gameId}`;
}

export const INSTALL_ID_COOKIE_NAME = '_sklz_iid';
export const INSTALLED_GAMES_LS_KEY = '_sklz_installed_games';
export const SKILLZ_APP_COOKIE_NAME = '_sklz_app_';
export const REGISTER_LOCK_COOKIE_NAME = '_sklz_rl';
export const SDK_DEPOSIT_COOKIE_NAME = '_sklz_sdk_deposit';
// Partner white-label: the resolved partner/tenant slug is persisted host-only in
// this cookie (never scoped to .skillz.com) and mirrored as a request header so
// server components can read it. See utils/resolveTenant + config/tenants.
export const PARTNER_COOKIE_NAME = '_sklz_partner';
export const PARTNER_HEADER = 'x-partner';
// Set once per tab (sessionStorage) by the partner/root SDK runtime after it mints the
// anon session, so it mints only once. Logout must clear it, or a same-tab reload after
// logout (sessionStorage survives navigation) sees the flag and never re-mints.
export const PARTNER_SESSION_MINT_FLAG = 'skillz.partner.session.bootstrapped';
export const ENVIRONMENT_COOKIE_NAME = '_sklz_env';
export const FTUE_COOKIE_NAME = '_sklz_ftue';
export const INTERSTITIAL_COOKIE_NAME = '_skz_i';
export const ADD_TO_HOME_STATE = 'ADD_TO_HOME_STATE';
export const CACHE_PREFIX = 'SKILLZ_URL_CACHE_';
export const USER_CACHE_KEY = `${CACHE_PREFIX}user`;
export const USER_CACHE_PREFIX = `${CACHE_PREFIX}users/`;

export const FTUE_STATE = {
  ANONYMOUS: '0',
  REGISTERED: '1',
  FINISHED: '2',
} as const;
export const DEV_CONSOLE_STAGE_URI = `http://portal-developer.internal.staging.cloud.skillz.com`;
export const DEV_CONSOLE_PROD_URI = `https://developers.skillz.com`;
export const ENCRYPTION_KEY = '7f1c3e5b9a4d8e7f2b3c9d0f4e6a1b7d8c2e4f7a5d3e6c1b9f4a8e7c2d1f0a3b'; // Must be 32 bytes

export const LEARN_BONUS_CASH_LINK =
  'https://support.skillz.com/hc/en-us/articles/203685769-How-does-Bonus-Cash-work-and-how-can-I-get-more-of-it';

export const CREDIT_CARD_TYPES = ['MASTERCARD', 'VISA', 'DISCOVER', 'AMEX'];

export const PAYMENT_METHOD_PAYPAL = 'paypal';
export const PAYMENT_METHOD_CREDIT_CARD = 'creditCard';
export const PAYMENT_METHOD_APPLE_PAY = 'applePay';

export const USE_BRAINTREE_DROPIN_UI = false;

export const E_PROTECT_LIB_STAGE_URL =
  'https://request.eprotect.vantivprelive.com/eProtect/js/eProtect-iframe-client4.min.js';

export const E_PROTECT_LIB_PROD_URL =
  'https://request.eprotect.vantivcnp.com/eProtect/js/eProtect-iframe-client4.min.js'; // change to post live after approval

/**
 * A mapping of all Skillz environments to their respective service URLs.
 * Used to resolve endpoints dynamically based on the current environment.
 */
export const CONFIG_BY_ENV: Record<SKZEnvironment, Record<string, string>> = {
  [SKZEnvironment.PRODUCTION]: {
    cdn: 'https://cdn.skillz.com',
    wowza: 'wss://wowzatls.production.cloud.skillz.com:443/webrtc-session.json',
    turn: 'turn.production.cloud.skillz.com',
    ACCESS_TOKEN_COOKIE_NAME: 'access_token',
    REFRESH_TOKEN_COOKIE_NAME: 'access_refresh_token',
    CURRENT_USER_COOKIE_NAME: '_sklz_cu',
    SOCIAL_CASINO_URL: 'https://games.skillz.com',
    SOCIAL_CASINO_GID: '23893',
    E_PROTECT_LIB: E_PROTECT_LIB_PROD_URL,
    E_PROTECT_PAYPAGE_ID: 'Y5sA6sF3xevzVvRn',
  },
  [SKZEnvironment.PRODUCTION_SANDBOX]: {
    cdn: 'https://cdn.skillz.com',
    wowza: '',
    ACCESS_TOKEN_COOKIE_NAME: 'production_sandbox_access_token',
    REFRESH_TOKEN_COOKIE_NAME: 'production_sandbox_refresh_token',
    CURRENT_USER_COOKIE_NAME: '_sklz_cu_production_sandbox',
    SOCIAL_CASINO_URL: 'https://games.skillz.com',
    SOCIAL_CASINO_GID: '23893',
    E_PROTECT_LIB: E_PROTECT_LIB_STAGE_URL,
    E_PROTECT_PAYPAGE_ID: '4ERTiGYXKm59YWfH',
  },
  [SKZEnvironment.STAGING]: {
    cdn: 'https://cdn.staging.skillz.com',
    wowza: 'wss://wowzatls.staging.cloud.skillz.com:443/webrtc-session.json',
    turn: 'turn.staging.cloud.skillz.com',
    ACCESS_TOKEN_COOKIE_NAME: 'staging_access_token',
    REFRESH_TOKEN_COOKIE_NAME: 'staging_refresh_token',
    CURRENT_USER_COOKIE_NAME: '_sklz_cu_staging',
    SOCIAL_CASINO_URL: 'https://staging.socialcasino.skillz.com',
    SOCIAL_CASINO_GID: '3616',
    E_PROTECT_LIB: E_PROTECT_LIB_STAGE_URL,
    E_PROTECT_PAYPAGE_ID: '4ERTiGYXKm59YWfH',
  },
  [SKZEnvironment.STAGING_SANDBOX]: {
    cdn: 'https://cdn.staging.skillz.com',
    wowza: '',
    ACCESS_TOKEN_COOKIE_NAME: 'staging_sandbox_access_token',
    REFRESH_TOKEN_COOKIE_NAME: 'staging_sandbox_refresh_token',
    CURRENT_USER_COOKIE_NAME: '_sklz_cu_staging_sandbox',
    SOCIAL_CASINO_URL: 'https://staging.socialcasino.skillz.com',
    SOCIAL_CASINO_GID: '3616',
    E_PROTECT_LIB: E_PROTECT_LIB_STAGE_URL,
    E_PROTECT_PAYPAGE_ID: '4ERTiGYXKm59YWfH',
  },
  [SKZEnvironment.QA]: {
    cdn: 'https://cdn.qa.skillz.com',
    wowza: 'wss://wowzatls.qa.cloud.skillz.com:443/webrtc-session.json',
    ACCESS_TOKEN_COOKIE_NAME: 'qa_access_token',
    REFRESH_TOKEN_COOKIE_NAME: 'qa_refresh_token',
    CURRENT_USER_COOKIE_NAME: '_sklz_cu_qa',
    SOCIAL_CASINO_URL: 'https://dev.socialcasino.skillz.com',
    SOCIAL_CASINO_GID: '3616',
    E_PROTECT_LIB: E_PROTECT_LIB_STAGE_URL,
    E_PROTECT_PAYPAGE_ID: '4ERTiGYXKm59YWfH',
  },
  [SKZEnvironment.QA_SANDBOX]: {
    cdn: 'https://cdn.qa.skillz.com',
    wowza: 'wss://wowzatls.qa.cloud.skillz.com:443/webrtc-session.json',
    ACCESS_TOKEN_COOKIE_NAME: 'qa_access_token',
    REFRESH_TOKEN_COOKIE_NAME: 'qa_refresh_token',
    CURRENT_USER_COOKIE_NAME: '_sklz_cu_qa',
    SOCIAL_CASINO_URL: 'https://dev.socialcasino.skillz.com',
    SOCIAL_CASINO_GID: '3616',
    E_PROTECT_LIB: E_PROTECT_LIB_STAGE_URL,
    E_PROTECT_PAYPAGE_ID: '4ERTiGYXKm59YWfH',
  }
};
